import type { ReactNode } from 'react'
import { useNls } from '../../../nls/nlsProvider'
import { Pressable } from '../../Common/Pressable'
import type { AttentionItem, ViewerTier } from '../../../utils/overviewInsights'

export interface AttentionSectionProps {
  tier: ViewerTier
  items: AttentionItem[]
  onItemAction?: (item: AttentionItem) => void
  toolsNode?: ReactNode
}

export const AttentionSection = ({
  tier,
  items,
  onItemAction,
  toolsNode,
}: AttentionSectionProps) => {
  const { t } = useNls()

  const visibleItems =
    tier === 'viewer' || tier === 'helper'
      ? items.filter((item) => item.kind !== 'person_overloaded' && item.kind !== 'person_quiet')
      : items

  const getToneColor = (severity: string) => {
    switch (severity) {
      case 'bad':
        return 'var(--danger)'
      case 'warn':
        return 'var(--warning)'
      case 'good':
        return 'var(--success)'
      case 'info':
      default:
        return 'var(--info)'
    }
  }

  return (
    <div className="overviewCard overviewAttn" data-testid="overview-attention-section">
      {toolsNode}
      <div className="overviewCard__header">
        <h3 className="overviewCard__title">
          {t('NLSOverviewAttentionTitle')}
          <span
            className={`overviewPill ${
              visibleItems.length > 0 ? 'overviewPill--bad' : 'overviewPill--neutral'
            }`}
          >
            {visibleItems.length}
          </span>
        </h3>
      </div>

      <div className="overviewAttnList">
        {visibleItems.map((item) => (
          <div key={item.id} className="overviewAttnItem" data-attention-item={item.id}>
            <div
              className="overviewAttnBar"
              style={{ background: getToneColor(item.severity) }}
            />
            <div className="overviewAttnContent">
              <div className="overviewAttnTitle">{item.title}</div>
              <div className="overviewAttnDetail">{item.detail}</div>
            </div>
            {onItemAction && (
              <Pressable
                type="button"
                className="overviewPage__toolBtn"
                onClick={() => onItemAction(item)}
              >
                →
              </Pressable>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
